import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Typography,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { userService, User } from '../services/users';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'staff.view');
  const canCreate = hasPermission(user, 'staff.create');

  useEffect(() => {
    if (!canView) {
      setUsers([]);
      return;
    }
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const usersData = await userService.getAll();
      setUsers(usersData);
    } catch {
      message.error('Failed to load users data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await userService.delete(id);
      message.success('User deleted');
      fetchData();
    } catch {
      message.error('Failed to delete user');
    }
  };

  const baseColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a: User, b: User) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      sorter: (a: User, b: User) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      sorter: (a: User, b: User) => (a.phone || '').localeCompare(b.phone || ''),
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 180,
      sorter: (a: User, b: User) => (a.department || '').localeCompare(b.department || ''),
      render: (department: string) => department || '-',
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 180,
      sorter: (a: User, b: User) => (a.position || '').localeCompare(b.position || ''),
      render: (position: string) => position || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a: User, b: User) => (a.status || 'active').localeCompare(b.status || 'active'),
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status || 'active'}
        </Tag>
      ),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: User) => record.name
            : col.dataIndex === 'email'
            ? (record: User) => record.email
            : col.dataIndex === 'phone'
            ? (record: User) => record.phone
            : col.dataIndex === 'department'
            ? (record: User) => record.department
            : col.dataIndex === 'position'
            ? (record: User) => record.position
            : col.dataIndex === 'status'
            ? (record: User) => record.status || 'active'
            : (undefined as unknown as (record: User) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<User>(users, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Users
        </Title>
        <Space>
          <ColumnVisibilityToggle
            columns={columnConfig}
            visibleKeys={visibleKeys}
            onChange={handleColumnVisibilityChange}
          />
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/users/new')}>
              New User
            </Button>
          )}
        </Space>
      </div>
      <Table
        columns={columnsWithSearch}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        pagination={false}
        components={{
          header: {
            cell: ResizableTableHeader,
          },
        }}
        footer={() => (
          <div style={{ textAlign: 'left', padding: '8px 0' }}>
            Total: {filteredData.length} {filteredData.length === 1 ? 'user' : 'users'}
          </div>
        )}
        onRow={(record) =>
          canView
            ? {
                onClick: () => navigate(`/users/${record.id}/edit`),
                style: { cursor: 'pointer' },
              }
            : {
                style: { cursor: 'default' },
              }
        }
      />
    </div>
  );
};
